import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'
const TIMEOUT  = Number(import.meta.env.VITE_API_TIMEOUT) || 120_000

export const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  timeout: TIMEOUT,
  headers: { 'Content-Type': 'application/json' },
})

const SYSTEM_RESERVED_PATHS = new Set([
  'login', 'dashboard', 'ia-chat', 'crm', 'companies', 'chat', 'courses-admin',
  'student-portal', 'marketplace', 'hr', 'finance', 'legal', 'website-designer',
  'webmail', 'settings', 'system-logs', 'reset-password', 'client-portal',
  'portal-cliente', 'portal-cliente-patrimonial', 'portal-cliente-escola',
  'portal-cliente-monitoramento', 'admin-patrimonial', 'admin-escola',
  'admin-monitoramento', 'admin-organica', 'patrimonial', 'organica', 'escola',
  'monitoramento', 'regulamentar', 'abertura', 'tenants', 'diario-oficial',
  'ecossistema', 'sandbox', 'output', 'api', 'uploads', 'whatsapp-admin',
  'profile', 'calendar', 'system', 'collaborators', 'payroll', 'contracts',
  'clientes', 'leads', 'prospeccao', 'campanhas', 'alvaras', 'aprovacao',
  'grade', 'matriculas', 'professores', 'categorias', 'banco-questoes', 'certificados',
  'escalas', 'vitrine', 'checkout', 'public', 'default'
])

interface NavigatorStandalone extends Navigator {
  standalone?: boolean
}

interface PlaywrightWindow extends Window {
  __PLAYWRIGHT__?: boolean
}

function getHandshakeToken(): string | null {
  const handshakeRaw = sessionStorage.getItem('gsp_imp_handshake')
  if (handshakeRaw) {
    try {
      const parsed = JSON.parse(handshakeRaw) as { createdAt?: number; accessToken?: string }
      if (parsed.createdAt && Date.now() - parsed.createdAt < 30_000) {
        return parsed.accessToken ?? null
      }
      sessionStorage.removeItem('gsp_imp_handshake')
    } catch {
      sessionStorage.removeItem('gsp_imp_handshake')
    }
  }

  if (window.location.hash.includes('impersonate_token=')) {
    return new URLSearchParams(window.location.hash.replace(/^#/, '')).get('impersonate_token')
  }

  return null
}

function getStoredAuthToken(): string | null {
  const isPWA =
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((window.navigator as NavigatorStandalone).standalone)
  const isPlaywright =
    Boolean((window as PlaywrightWindow).__PLAYWRIGHT__) ||
    Boolean(localStorage.getItem('isPlaywright'))
  const storage = isPWA || isPlaywright
    ? localStorage.getItem('gsp-auth')
    : sessionStorage.getItem('gsp-auth')

  const activeStorage = storage || sessionStorage.getItem('gsp-auth') || localStorage.getItem('gsp-auth')
  if (!activeStorage) {
    return null
  }

  const parsed = JSON.parse(activeStorage) as { state?: { tokens?: { accessToken?: string } } }
  return parsed.state?.tokens?.accessToken ?? null
}

function resolveRequestToken(): string | null {
  try {
    const impToken = getHandshakeToken()
    if (impToken && (window.location.pathname === '/login' || window.location.pathname === '/')) {
      return impToken
    }
    return getStoredAuthToken()
  } catch (e) {
    console.warn('Erro ao ler token do sessionStorage', e)
    return null
  }
}

function decodeJwtTenant(token: string | null): { tenantId?: string; isExplicitPublicToken: boolean } {
  if (!token) {
    return { isExplicitPublicToken: false }
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { tenantId?: string }
    if (payload.tenantId && payload.tenantId !== 'public') {
      return { tenantId: payload.tenantId, isExplicitPublicToken: false }
    }
    if (payload.tenantId === 'public') {
      return { isExplicitPublicToken: true }
    }
  } catch (e) {
    console.warn('Erro ao decodificar token JWT', e)
  }
  return { isExplicitPublicToken: false }
}

function resolveFallbackTenantId(): string | undefined {
  const pathname = window.location.pathname
  const searchParams = new URLSearchParams(window.location.search)
  const tenantParam = searchParams.get('tenant')
  const firstSegment = pathname.split('/').find(Boolean)?.toLowerCase()

  const storedSlug = localStorage.getItem('tenantSlug')
  if (storedSlug && SYSTEM_RESERVED_PATHS.has(storedSlug.toLowerCase())) {
    localStorage.removeItem('tenantSlug')
    sessionStorage.removeItem('tenantSlug')
  }

  if (tenantParam && !SYSTEM_RESERVED_PATHS.has(tenantParam.toLowerCase())) {
    return tenantParam
  }

  if (firstSegment && !SYSTEM_RESERVED_PATHS.has(firstSegment)) {
    return firstSegment
  }

  if (window.location.hostname !== 'localhost' && !window.location.hostname.includes('gspassessoria.com.br')) {
    return window.location.hostname
  }

  const activeSlug = localStorage.getItem('tenantSlug')
  if (activeSlug && !SYSTEM_RESERVED_PATHS.has(activeSlug.toLowerCase())) {
    return activeSlug
  }

  return undefined
}

function getHeaderValue(config: InternalAxiosRequestConfig, key: string): string | undefined {
  if (!config.headers) {
    return undefined
  }
  if (typeof config.headers.get === 'function') {
    const val = config.headers.get(key)
    return typeof val === 'string' ? val : undefined
  }
  const direct = config.headers[key]
  return typeof direct === 'string' ? direct : undefined
}

function setHeaderValue(config: InternalAxiosRequestConfig, key: string, value: string): void {
  if (config.headers && typeof config.headers.set === 'function') {
    config.headers.set(key, value)
  } else if (config.headers) {
    config.headers[key] = value
  }
}

function clearAuthorizationHeader(config: InternalAxiosRequestConfig): void {
  if (config.headers && typeof config.headers.delete === 'function') {
    config.headers.delete('Authorization')
  } else if (config.headers) {
    delete config.headers['Authorization']
  }
}

// ── Request Interceptor: inject access token ──────────────
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = resolveRequestToken()
    if (token) {
      setHeaderValue(config, 'Authorization', `Bearer ${token}`)
    }

    const explicitTenantHeader = getHeaderValue(config, 'x-tenant-id')
    if (!explicitTenantHeader) {
      const { tenantId: jwtTenantId, isExplicitPublicToken } = decodeJwtTenant(token)
      const shouldCheckFallback =
        !jwtTenantId &&
        (!isExplicitPublicToken ||
          Boolean(config.url?.includes('/auth/login')) ||
          Boolean(config.url?.includes('/tenant/resolve')))

      const effectiveTenantId = jwtTenantId ?? (shouldCheckFallback ? resolveFallbackTenantId() : undefined)
      if (effectiveTenantId) {
        setHeaderValue(config, 'x-tenant-id', effectiveTenantId)
      }
    }

    return config
  },
  (error) => Promise.reject(error),
)

// ── Response Interceptor: handle 401 → refresh ───────────
let isRefreshing = false
let failedQueue: Array<{ resolve: (v: unknown) => void; reject: (e: unknown) => void }> = []

const processQueue = (error: unknown) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(null)))
  failedQueue = []
}

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean }

function isAuthBypassUrl(url?: string): boolean {
  if (!url) {
    return false
  }
  return (
    url.includes('/auth/refresh') ||
    url.includes('/auth/login') ||
    url.includes('/auth/impersonate/revert')
  )
}

async function handleUnauthorizedRetry(originalRequest: RetryableRequestConfig) {
  if (isRefreshing) {
    await new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject })
    })
    originalRequest._retry = true
    clearAuthorizationHeader(originalRequest)
    return api(originalRequest)
  }

  originalRequest._retry = true
  isRefreshing = true

  try {
    const { useAuthStore } = await import('@store/authStore')
    await useAuthStore.getState().refreshTokens()
    processQueue(null)
    clearAuthorizationHeader(originalRequest)
    return api(originalRequest)
  } catch (refreshError) {
    processQueue(refreshError)
    const { useAuthStore } = await import('@store/authStore')
    useAuthStore.getState().logout()
    throw refreshError
  } finally {
    isRefreshing = false
  }
}

function notifyBadRequestIssues(data?: { issues?: Array<{ path?: string; message: string }>; error?: string }): void {
  if (Array.isArray(data?.issues)) {
    const issues = data.issues
    void import('react-hot-toast').then(({ default: toast }) => {
      issues.forEach((issue) => {
        const field = issue.path ? issue.path.replace('body.', '') : ''
        const prefix = field ? `[${field.toUpperCase()}] ` : ''
        toast.error(`${prefix}${issue.message}`)
      })
    })
  } else if (data?.error) {
    const errorMsg = data.error
    void import('react-hot-toast').then(({ default: toast }) => {
      toast.error(errorMsg)
    })
  }
}

api.interceptors.response.use(
  (response) => {
    const newToken = response.headers?.['x-new-access-token'] || response.headers?.['X-New-Access-Token']
    if (newToken && typeof newToken === 'string') {
      void import('@store/authStore')
        .then(({ useAuthStore }) => {
          useAuthStore.getState().setAccessToken(newToken)
        })
        .catch((err) => {
          console.warn('Silent token update skipped:', err)
        })
    }
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthBypassUrl(originalRequest.url)) {
      return handleUnauthorizedRetry(originalRequest)
    }

    if (error.response?.status === 404) {
      const errData = error.response.data as { error?: string } | undefined
      if (errData?.error?.toLowerCase().includes('tenant not found')) {
        localStorage.removeItem('tenantSlug')
        sessionStorage.removeItem('tenantSlug')
      }
    }

    if (error.response?.status === 400) {
      const data = error.response.data as { issues?: Array<{ path?: string; message: string }>; error?: string } | undefined
      notifyBadRequestIssues(data)
    }

    throw error
  },
)
